"use client";

import { useEffect, useState } from "react";
import { hotjar } from "react-hotjar";
import LinkedInTag from "react-linkedin-insight";
import { useIntercom } from "react-use-intercom";
import { usePathname } from "next/navigation";

// helpers
import useUserProfile from "@/hooks/useUserProfile";

declare global {
  interface Window {
    OptanonWrapper: () => void;
    gtag: (command: string, obj: unknown) => void;
    OptanonActiveGroups: string[];
    dataLayer: Record<string, unknown>[];
  }
}

const mapSourceValue: { [key: string]: string } = {
  "WERI": "West",
  "LOCL": "Core"
};


const ConnectThirdPartyJs = () => {
  const [cookieConfig, setCookieConfig] = useState({
    isStrictlyNecessary: true,
    isPerformance: true,
    isFunctional: true,
    isTargeting: true
  });

  const { data: userProfile } = useUserProfile();
  const { update, shutdown, boot } = useIntercom();

  const source = mapSourceValue[process.env.NEXT_PUBLIC_APP_WL_CODE || "LOCL"];

  const pathname = usePathname();

  // methods
  const initHotjar = () => {
    if (process.env.NEXT_PUBLIC_HOTJAR_ID && cookieConfig.isFunctional) {
      hotjar.initialize({
        id: process.env.NEXT_PUBLIC_HOTJAR_ID as unknown as number,
        sv: 6,
      });
    }
  };

  const initGaUser = () => {
    if (userProfile && cookieConfig.isPerformance && window?.gtag) {
      window.gtag("set", { user_id: userProfile.id });
      window.gtag("set", { dimension1: userProfile.type });
    }
  };

  const initGA = () => {
    if (cookieConfig.isPerformance && window?.gtag) {
      window.gtag("pageview", window.location.pathname + window.location.search);
      window.gtag("send", { hitType: "pageview", page: "/" });
    }
  }

  const initLinkedIn = () => {
    LinkedInTag.init(
      process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID as string,
      "dc",
      cookieConfig.isTargeting && (!!process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID)
    );
  };

  const initGoogleTagManager = (gaId: string) => {
    if (!cookieConfig.isPerformance || !gaId) {
      return;
    }

    // Ensure the dataLayer array exists
    window.dataLayer = window.dataLayer || [];

    // Push the initial event to the dataLayer
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js"
    });

    window.gtag = window.gtag || function() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments as unknown as Record<string, unknown>)
    }
  }

  // effects
  useEffect(() => {
    initHotjar();
    process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID && initLinkedIn();
    initGoogleTagManager(process.env.NEXT_PUBLIC_G_TAG_ID as string);
    initGA()
  }, [JSON.stringify(cookieConfig)]);

  useEffect(() => {
    initGaUser();
  }, [JSON.stringify(userProfile), JSON.stringify(cookieConfig)]);

  useEffect(() => {
    if (typeof window !== "undefined" && !window.OptanonWrapper) {
      window.OptanonWrapper = () => {
        window.OneTrust?.OnConsentChanged(() => {
          setCookieConfig({
            isStrictlyNecessary: window.OptanonActiveGroups.includes("C0001"),
            isPerformance: window.OptanonActiveGroups.includes("C0002"),
            isFunctional: window.OptanonActiveGroups.includes("C0003"),
            isTargeting: window.OptanonActiveGroups.includes("C0004")
          });
        });
      }
    }
  }, []);

  useEffect(() => {
    try {
      if (userProfile?.id) {
        fetch(`/api/hmac-code/${userProfile.id}`).then(resp => resp.json().then(data => {
          update({
            userId: `${userProfile.id || Date.now()}`,
            email: userProfile.email,
            name: [userProfile.firstName, userProfile.lastName].filter(Boolean).join(" "),
            "source": source,
            userHash: data.hash,
            verticalPadding: 80,
          } as unknown as Record<string, string>);
        }))
      } else {
        shutdown();
        boot({
          verticalPadding: 80,
        });
      }
    } catch (e) {
      console.error("Intercom error:", e);
    }
  }, [userProfile?.id]);

  useEffect(() => {
    const regex = /^\/(uk|aus)\/lp(?:\/.*)?$/;

    if (regex.test(pathname)) {
      update({
        hideDefaultLauncher: true
      })
    } else {
      update({
        hideDefaultLauncher: false
      })
    }
  }, [pathname])

  return (<></>)
};

export default ConnectThirdPartyJs;
